import { useCallback } from 'react';
import { getPartner } from '@/data/partnersRepo';
import { useAutosavePartner } from '@/hooks/autosave/useAutosavePartner';
import { useSaveStack } from '@/hooks/autosave/util_saveStack';
import { PARTNER_TERMS_VERSION, type PartnerProfileDraftType } from '@/types/types_partner';

/**
 * Writes a completed onboarding form to the partner record: the profile fields
 * plus the terms acceptance, in one update.
 *
 * Deliberately routed through `useAutosavePartner` rather than straight at
 * `updatePartnerFields`, so onboarding lands in the document's history like
 * every other partner edit — an admin reading the log sees where the record came
 * from instead of a document that arrived fully-formed.
 *
 * One write, not two: a manager who accepted the terms but whose profile write
 * failed would be counted as done by `partnerOnboardingComplete` while the
 * record was still half empty.
 *
 * The history is re-read here rather than passed in, because neither caller can
 * be trusted to have it. On the invitation page the record has never been read
 * at all — the visitor had no claim to read it with until a moment ago — and a
 * missing history would restart the step counter at 1, overwriting the steps an
 * admin's own edits had left behind.
 */
export function useSubmitPartnerOnboarding(partnerId: string | undefined) {
  const saveStack = useSaveStack();
  const autosave = useAutosavePartner(partnerId, saveStack);

  return useCallback(
    async (draft: PartnerProfileDraftType, uid: string): Promise<void> => {
      if (!partnerId) throw new Error('Cannot save onboarding: no partner id.');
      const existing = await getPartner(partnerId);
      const { saved } = autosave(
        'Completed partner registration',
        {
          ...draft,
          termsAcceptedAt: new Date().toISOString(),
          termsAcceptedByUid: uid,
          termsVersion: PARTNER_TERMS_VERSION,
        },
        existing?.history,
      );
      await saved;
    },
    [partnerId, autosave],
  );
}
